
import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { ErrorResponse } from "@/common/types/errorResponse";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { getRailsUpdate } from "@/common/utils/getRailsUpdate";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";
import { GovernmentAllocationDistributionsLimitsData } from "../../types/governmentAllocation.type";
import { ProductCategoryDistributionLimitControlledByCurrencyValidationSchema, ProductCategoryDistributionLimitControlledByQuantityValidationSchema, ProductCategoryDistributionLimitValidationSchema } from "../../schemas/distributionsLimits.schema";

interface UseUpdateGovernmentAllocationDistributionLimitProps {
  governmentAllocationId?: string;
  productCategoryDistributionLimit: GovernmentAllocationDistributionsLimitsData['product_category_distribution_limits'][number];
  controlledBy?: CompanyContractConsumptionControlledBy;
  onSuccess?: () => void;
  onError?: (error: ErrorResponse) => void;
}

export const useUpdateGovernmentAllocationDistributionLimit = ({
  governmentAllocationId,
  productCategoryDistributionLimit,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
  onSuccess,
  onError,
}: UseUpdateGovernmentAllocationDistributionLimitProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateGovernmentAllocationDistributionLimit', governmentAllocationId],
    mutationFn: (data: ProductCategoryDistributionLimitValidationSchema) => {
      let payload;

      if (controlledBy === CompanyContractConsumptionControlledBy.Currency) {
        data = data as ProductCategoryDistributionLimitControlledByCurrencyValidationSchema;

        payload = {
          id: productCategoryDistributionLimit.id,
          maximum_value: unFormatCurrency(data.maximum_value),
          product_distribution_limits_attributes: [
            ...getRailsUpdate(
              productCategoryDistributionLimit.product_distribution_limits || [],
              data.product_distribution_limits_attributes || [],
              (x) => x.product.id,
              (y) => y.product.id,
              mapToDeletion,
              (productDistributionLimit) => ({
                product_id: productDistributionLimit.product.id,
                maximum_value: unFormatCurrency(productDistributionLimit.maximum_value),
              }),
            ),
            ...data.product_distribution_limits_attributes.filter(p => p.id).map((productDistributionLimit) => ({
              id: productDistributionLimit.id,
              maximum_value: unFormatCurrency(productDistributionLimit.maximum_value),
            })),
          ],
        }
      } else {
        data = data as ProductCategoryDistributionLimitControlledByQuantityValidationSchema;

        payload = {
          id: productCategoryDistributionLimit.id,
          maximum_quantity: unFormatUnknownUnit(data.maximum_quantity),
          product_distribution_limits_attributes: [
            ...getRailsUpdate(
              productCategoryDistributionLimit.product_distribution_limits || [],
              data.product_distribution_limits_attributes || [],
              (x) => x.product.id,
              (y) => y.product.id,
              mapToDeletion,
              (z) => ({
                product_id: z.product.id,
                maximum_quantity: unFormatCurrency(z.maximum_quantity),
              }),
            ),
            ...data.product_distribution_limits_attributes.filter(p => p.id).map((productDistributionLimit) => ({
              id: productDistributionLimit.id,
              maximum_quantity: unFormatCurrency(productDistributionLimit.maximum_quantity),
            })),
          ],
        }
      }

      return privateAxios.put(`/billing/government_allocations/${governmentAllocationId}/update_distribution_limits`, {
        product_category_distribution_limits_attributes: [payload],
      });
    },
    onSuccess,
    onError,
  });
}
